'use client';

import { useMemo } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { AlertCircleIcon } from '@terra/ui-icons';

type LoginRendererProps = {
  data: unknown;
  maxHeight?: string;
};
type ParsedLoginData = {
  username?: string;
  role?: string;
  message?: string;
  success?: boolean;
  error?: string;
};

function parseLoginData(data: unknown): ParsedLoginData {
  if (typeof data === 'string') {
    const parsed: ParsedLoginData = { success: true };
    const authenticatedAsMatch = data.match(
      /Authenticated\s+as\s+(\S+)(?:\s*\(([^)]+)\))?/i,
    );

    if (authenticatedAsMatch) {
      parsed.username = authenticatedAsMatch[1].replace(/[.,]$/, '');
      if (authenticatedAsMatch[2]) {
        parsed.role = authenticatedAsMatch[2];
      }
    }

    if (!parsed.username) {
      const usernameMatch = data.match(/username\s+(\S+)/i);

      if (usernameMatch) {
        parsed.username = usernameMatch[1].replace(/[.,]$/, '');
      }
    }

    if (!parsed.role) {
      const roleMatch = data.match(/role\s+(\S+)/i);

      if (roleMatch) {
        parsed.role = roleMatch[1].replace(/[.,]$/, '');
      }
    }

    if (
      data.toLowerCase().includes('error') ||
      data.toLowerCase().includes('failed')
    ) {
      parsed.success = false;
      parsed.error = data;
    }

    parsed.message = data;

    return parsed;
  }

  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;

    return {
      username: typeof obj.username === 'string' ? obj.username : undefined,
      role: typeof obj.role === 'string' ? obj.role : undefined,
      message: typeof obj.message === 'string' ? obj.message : undefined,
      success: obj.success !== false && !obj.error,
      error: typeof obj.error === 'string' ? obj.error : undefined,
    };
  }

  return {};
}

export function LoginRenderer({ data }: LoginRendererProps) {
  const parsedData = useMemo(() => parseLoginData(data), [data]);
  const { success, error, message } = parsedData;
  const hasError = !success || !!error;

  if (!hasError) {
    return null;
  }

  const errorMessage = error || message || 'Authentication failed';

  return (
    <Box width="100%" p={2}>
      <HStack gap={2} alignItems="flex-start">
        <Box color="fg.error" flexShrink={0} pt={0.5}>
          <AlertCircleIcon size={14} />
        </Box>
        <Text
          fontSize="xs"
          color="fg.error"
          fontFamily="mono"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {errorMessage}
        </Text>
      </HStack>
    </Box>
  );
}
